import type { Recipe } from './types.js';
import { filterRecipes } from './recipeFilter.js';
import type { RecipeFilter } from './recipeFilter.js';

export const RECIPE_CACHE_KEY = 'chefcook:recipes:catalog:v1';

interface RecipeCachePayload {
  savedAt: number;
  recipes: Recipe[];
}

function isRecipe(value: unknown): value is Recipe {
  if (value === null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.id === 'number' &&
    typeof r.title === 'string' &&
    typeof r.meal_type === 'string' &&
    typeof r.portions === 'number' &&
    typeof r.prep_time_minutes === 'number' &&
    typeof r.instructions === 'string' &&
    Array.isArray(r.tags) &&
    Array.isArray(r.ingredients) &&
    typeof r.per_portion === 'object' &&
    r.per_portion !== null
  );
}

export function readRecipeCache(): Recipe[] | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(RECIPE_CACHE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const payload = parsed as Partial<RecipeCachePayload>;
  if (!Array.isArray(payload.recipes)) return null;
  const valid = payload.recipes.filter(isRecipe);
  return valid;
}

export function writeRecipeCache(recipes: Recipe[]): void {
  const payload: RecipeCachePayload = { savedAt: Date.now(), recipes };
  try {
    localStorage.setItem(RECIPE_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // best-effort cache; quota / disabled storage errors are non-fatal
  }
}

export function clearRecipeCache(): void {
  try {
    localStorage.removeItem(RECIPE_CACHE_KEY);
  } catch {
    // ignore
  }
}

export function filterCachedRecipes(f: RecipeFilter): Recipe[] {
  const cached = readRecipeCache() ?? [];
  return filterRecipes(cached, f);
}
